import { createContext, useCallback } from "react";
import axios, { AxiosError } from "axios";
import { getApiUrl } from "/@/config";
import Cookies from "js-cookie";
import { notifications } from "@mantine/notifications";

import { getDomain, setFeatureFlagEvaluationProperties } from "@jasper/shared";
import { usePostHog } from "posthog-js/react";

export const UserContext = createContext();

export const UseAuth = () => {
  const postHog = usePostHog();

  const me = async () => {
    try {
      const usr = await axios.get(`${getApiUrl()}/users/me`, {
        withCredentials: true,
      });
      if (!usr || !usr.data) {
        return null;
      }
      clearCookies();
      const domain = getDomain(
        import.meta.env.MODE,
        import.meta.env.VITE_API_URL
      );
      Cookies.set("Authentication", usr.data.accessToken, { domain: domain });
      Cookies.set("email", usr.data.email, { domain: domain });
      Cookies.set("userGroupId", usr.data.userGroupId, { domain: domain });
      Cookies.set("id", usr.data.id, { domain: domain });
      Cookies.set("role", usr.data.role, { domain: domain });
      Cookies.set("type", usr.data.type, { domain: domain });
      Cookies.set(
        "allowedToEditProviderAccess",
        usr.data.allowedToEditProviderAccess,
        { domain: domain }
      );
      if (usr.data.refreshToken.split("Refresh=").length > 1) {
        Cookies.set(
          "Refresh",
          usr.data.refreshToken.split("Refresh=")[1].split(";")[0],
          { domain: domain }
        );
      }

      identifyUser(usr.data);

      return usr.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const login = async (data: {
    email: string;
    password: string;
    newPassword?: string;
  }): Promise<boolean> => {
    try {
      const req = await axios.post(`${getApiUrl()}/auth/login`, data);
      clearCookies();
      const domain = getDomain(
        import.meta.env.MODE,
        import.meta.env.VITE_API_URL
      );
      Cookies.set("email", data.email, { domain: domain });
      Cookies.set("userGroupId", req.data.userGroupId, { domain: domain });
      Cookies.set("id", req.data.id, { domain: domain });
      Cookies.set("role", req.data.role, { domain: domain });
      Cookies.set("type", req.data.type, { domain: domain });
      Cookies.set("Authentication", req.data.accessToken, { domain: domain });
      Cookies.set(
        "allowedToEditProviderAccess",
        req.data.allowedToEditProviderAccess,
        { domain: domain }
      );
      if (req.data.refreshToken.split("Refresh=").length > 1) {
        Cookies.set(
          "Refresh",
          req.data.refreshToken.split("Refresh=")[1].split(";")[0],
          { domain: domain }
        );
      }

      identifyUser(req.data);

      return req.data;
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 401) {
        notifications.show({
          title: "Wrong email or password",
          color: "red",
          message: "",
        });
        console.error(e);
        return false;
      }

      throw e;
    }
  };

  const getUser = () => {
    const user = {
      id: Cookies.get("id"),
      email: Cookies.get("email"),
      role: Cookies.get("role"),
      type: Cookies.get("type"),
      userGroupId: Cookies.get("userGroupId"),
      allowedToEditProviderAccess:
        Cookies.get("allowedToEditProviderAccess") === "true",
    };

    return user;
  };

  const identifyUser = useCallback(
    (user: ReturnType<typeof getUser>) => {
      if (!user || !user.id || !user.email) {
        return;
      }

      postHog.identify(user.id, {
        email: user.email,
        type: user.type,
        role: user.role,
        environment: import.meta.env.MODE,
      });

      if (user.userGroupId) {
        postHog.group("user_group", user.userGroupId);
      }

      setFeatureFlagEvaluationProperties(postHog);
    },
    [postHog]
  );

  const logOut = async (): Promise<boolean> => {
    try {
      await axios.get(`${getApiUrl()}/auth/logout`, { withCredentials: true });
      clearCookies();
      postHog.reset();
      setFeatureFlagEvaluationProperties(postHog);
      return true;
    } catch (error) {
      console.warn("Logout request failed", error);
      return false;
    }
  };

  const clearCookies = () => {
    const domain = getDomain(
      import.meta.env.MODE,
      import.meta.env.VITE_API_URL
    );
    Cookies.remove("id", { domain: domain });
    Cookies.remove("email", { domain: domain });
    Cookies.remove("role", { domain: domain });
    Cookies.remove("type", { domain: domain });
    Cookies.remove("Refresh", { domain: domain });
    Cookies.remove("Authentication", { domain: domain });
    Cookies.remove("userGroupId", { domain: domain });
    Cookies.remove("allowedToEditProviderAccess", { domain: domain });
  };

  return {
    user: getUser(),
    login,
    me,
    logOut,
    identifyUser,
  };
};
